/**
 * Centralized marketing copy/structure for Landing Page v2.
 *
 * docs/12-landing-page-brief.md and docs/18-pricing-storage-lock.md are the
 * product/commercial sources of truth. Keep claims here within the publication
 * gate: launch/planned capabilities must not be presented as already live.
 */

export const loginUrl = "https://app.dataprotection.work/login";
export const signupUrl = "https://app.dataprotection.work/signup";

export const primaryNav = [
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Features", href: "/#capabilities" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Resources", href: "/resources" },
] as const;

export const problemPoints = [
  {
    title: "Which privacy rules may apply",
    body: "A business can operate in one country, serve people in another, and use vendors somewhere else. Knowing which privacy framework to start with is not obvious.",
  },
  {
    title: "How to turn requirements into work",
    body: "Legal obligations are useful only when someone can translate them into concrete records, owners, actions, deadlines and review dates.",
  },
  {
    title: "What records and evidence to keep",
    body: "Privacy work often lives across spreadsheets, shared drives, email threads and source systems, making it hard to show what was actually done.",
  },
  {
    title: "How to manage website consent",
    body: "Cookies, analytics, marketing tools and consent preferences can introduce a separate layer of privacy work that still needs to connect back to the same governance process.",
  },
  {
    title: "How to handle requests and incidents",
    body: "A privacy request or incident quickly creates dates, decisions, communications and evidence that need to stay organized from intake through closure.",
  },
  {
    title: "What needs attention next",
    body: "Vendor reviews, retention dates, open risks and privacy actions are easy to miss when there is no single prioritized view of the work.",
  },
] as const;

export const howItWorksSteps = [
  {
    step: "1",
    title: "Tell us about your business",
    body: "Answer plain-language questions about where you operate, the people whose data you handle, your markets, processing locations, websites and risk indicators.",
  },
  {
    step: "2",
    title: "See what may apply",
    body: "Versioned rules recommend the relevant privacy modules and explain why they were activated. The result is guidance, not a binding legal determination.",
  },
  {
    step: "3",
    title: "Work through the plan",
    body: "Complete guided assessments, records, documents, vendor reviews, requests, incidents and website privacy tasks from one workspace.",
  },
  {
    step: "4",
    title: "Keep evidence and stay ready",
    body: "Track evidence links, reviews, retention, deadlines and the next action that needs attention as your privacy program changes over time.",
  },
] as const;

export const capabilities = [
  {
    title: "Privacy Readiness & Jurisdiction Onboarding",
    body: "Capture factual business and processing context, recommend the privacy frameworks that may matter, and explain why each module was activated.",
    status: "Launch module",
  },
  {
    title: "Know Your Data",
    body: "Build a plain-language inventory of the personal data your business handles, why it is used, where it comes from, where it goes and which systems or vendors touch it.",
    status: "Launch module",
  },
  {
    title: "Check Your Risks",
    body: "Use guided, deterministic assessments to surface privacy risks, actions and situations where a more formal impact assessment may be appropriate.",
    status: "Launch module",
  },
  {
    title: "Create Privacy Documents",
    body: "Generate supported privacy documents from structured workspace information without treating generated text as a guarantee of legal compliance.",
    status: "Launch module",
  },
  {
    title: "Manage Vendors",
    body: "Track processors, cloud services and contractors, what data they handle, review dates, agreements, concerns and follow-up actions.",
    status: "Launch module",
  },
  {
    title: "Handle Privacy Requests",
    body: "Track supported data-subject or privacy requests from receipt through response, evidence and closure, with jurisdiction-specific guidance where relevant.",
    status: "Launch module",
  },
  {
    title: "Respond to Incidents",
    body: "Document privacy incidents, affected information, containment, assessments, decisions, notifications, evidence, corrective actions and closure.",
    status: "Launch module",
  },
  {
    title: "Website Privacy & Consent",
    body: "Manage websites, consent configuration, preference choices, version evidence and jurisdiction-aware website privacy work from the same privacy workspace.",
    status: "Launch module",
  },
  {
    title: "Tasks, Reviews & Retention",
    body: "See overdue work, unresolved risks, approaching reviews, retention dates and the next privacy action that needs attention.",
    status: "Launch module",
  },
] as const;

export const priorityActions = [
  {
    priority: "HIGH" as const,
    title: "Define how long you keep customer information",
    why: "Keeping information indefinitely creates unnecessary risk.",
  },
  {
    priority: "HIGH" as const,
    title: "Document lawful basis for key processing",
    why: "The reason for processing should be explicit and reviewable.",
  },
  {
    priority: "MED" as const,
    title: "Configure website privacy and consent",
    why: "Website tracking and preferences should match the active privacy framework.",
  },
] as const;

export const launchJurisdictions = [
  {
    name: "Philippines / PH DPA",
    status: "Launch pack",
    body: "Philippine Data Privacy Act and NPC-oriented guidance, assessments, records and workflows for small-business privacy operations.",
  },
  {
    name: "GDPR / EU",
    status: "Launch pack",
    body: "GDPR-oriented applicability, readiness and operational guidance using factual establishment, offering and monitoring questions rather than citizenship alone.",
  },
] as const;

export const plannedJurisdictions = [
  {
    name: "Canada",
    body: "Planned federal foundation with provincial overlays where applicable.",
  },
  {
    name: "United States",
    body: "Planned core framework with state overlays, beginning with major regimes such as California.",
  },
] as const;

export const websiteConsentPoints = [
  "Register and manage website/domain privacy configuration",
  "Classify cookie/tracker categories and configure consent preferences",
  "Maintain versioned consent configuration and preference evidence",
  "Apply website privacy guidance from active jurisdiction packs",
  "Keep system-generated consent events separate from normal record quotas",
] as const;

export const pricingPlans = [
  {
    name: "Free",
    price: "$0",
    annual: "Free",
    badge: "Start here",
    tagline: "A useful privacy workspace for smaller workloads.",
    storage: "0 GB attachments",
    availability: "Free plan",
    points: [
      "All generally available core features at product launch",
      "Up to 5 customer-created records per feature/module",
      "1 successful privacy-document generation per workspace each calendar month",
      "External evidence/proof links",
      "No direct evidence/document uploads",
      "1-year default maximum platform retention for applicable records",
    ],
  },
  {
    name: "Pro",
    price: "$19/month",
    annual: "$190/year",
    badge: "Launch pricing",
    tagline: "For a small business running privacy as an ongoing process.",
    storage: "5 GB/workspace",
    availability: "Paid checkout coming soon",
    points: [
      "Higher customer-created record limits than Free",
      "Higher privacy-document generation allowance than Free",
      "5 GB customer-uploaded evidence/document attachment allowance at paid launch",
      "External evidence/proof links remain supported",
      "Up to 5-year default maximum platform retention for applicable records",
    ],
  },
  {
    name: "Business",
    price: "$49/month",
    annual: "$490/year",
    badge: "Launch pricing",
    tagline: "For growing organizations with heavier privacy operations.",
    storage: "25 GB/workspace",
    availability: "Paid checkout coming soon",
    points: [
      "Higher operational capacity than Pro",
      "Higher privacy-document generation allowance than Pro",
      "25 GB customer-uploaded evidence/document attachment allowance at paid launch",
      "External evidence/proof links remain supported",
      "Up to 5-year default maximum platform retention for applicable records",
    ],
  },
] as const;

export const storageAddon = {
  price: "$10/month",
  amount: "+5 GB",
  copy: "Add 5 GB of customer-uploaded attachment capacity for $10/month per block. Storage add-ons increase attachment capacity only; they do not automatically increase record, document-generation, consent-event, website, seat or retention limits.",
} as const;

export const trustPoints = [
  {
    title: "Privacy by design",
    body: "The product model favors structured privacy records, minimized content and explicit lifecycle controls instead of collecting data simply because storage is available.",
  },
  {
    title: "Data minimization",
    body: "Use categories, summaries, source-system references, pseudonymous identifiers and evidence links where they can do the job without copying underlying personal data.",
  },
  {
    title: "Cloudflare-first architecture",
    body: "The public site is deployed on Cloudflare Pages. The application architecture is designed around Cloudflare services with tenant isolation and controlled access boundaries.",
  },
  {
    title: "Mandatory 2FA direction",
    body: "The authenticated product is designed to require two-factor authentication rather than relying on a password alone.",
  },
  {
    title: "Workspace content is not for sale",
    body: "Customer privacy-workspace content is not sold, and billing providers do not receive assessment, incident, request, consent or document content merely to process subscriptions.",
  },
  {
    title: "Freemius handles billing",
    body: "Freemius is the planned Merchant of Record for paid subscriptions. DataProtection.work does not need to store payment-card details to manage product entitlements.",
  },
  {
    title: "Guidance, not legal advice",
    body: "DataProtection.work provides structured, practical guidance. It does not make a binding legal determination or guarantee legal or regulatory compliance.",
  },
] as const;

export const faqItems = [
  {
    question: "Is DataProtection.work free?",
    answer:
      "Yes. The Free plan is $0 and is designed to include all generally available core features at product launch, subject to the Free capacity, generation, upload and retention limits.",
  },
  {
    question: "What is included in Free?",
    answer:
      "Free includes up to 5 customer-created records per feature or module, 1 successful privacy-document generation per workspace per calendar month, external evidence or proof links, no direct evidence/document uploads, and a 1-year default maximum platform retention period for applicable records.",
  },
  {
    question: "What counts toward the 5-record limit?",
    answer:
      "The limit applies to customer-created privacy-management objects such as vendors, incidents, privacy requests, assessments or managed websites where the feature is record-based. System-generated consent, audit, security and operational events use separate quotas and do not consume those five records.",
  },
  {
    question: "What are the Pro and Business prices?",
    answer:
      "Locked launch pricing is $19/month or $190/year per workspace for Pro, and $49/month or $490/year per workspace for Business. Paid checkout is not represented as available until the subscription flow is implemented and verified.",
  },
  {
    question: "How much attachment storage is included?",
    answer:
      "Free includes 0 GB of direct attachment storage and uses evidence links instead. Pro includes a 5 GB customer-uploaded attachment allowance per workspace at paid launch, while Business includes 25 GB per workspace.",
  },
  {
    question: "Can I buy more attachment storage?",
    answer:
      "Yes for paid plans at launch: additional customer-uploaded attachment capacity is priced at $10/month per extra 5 GB block. The add-on increases attachment storage only, not other plan limits.",
  },
  {
    question: "Can I upload my customer or employee database?",
    answer:
      "No. DataProtection.work is not a CRM, HRIS, customer database, file repository or backup service. Do not upload or import entire customer databases, CRM exports, mailing lists, employee databases or similar bulk collections. Where a supported request, incident, complaint, consent or similar monitoring workflow genuinely needs person-level information, enter only the minimum reasonably necessary information.",
  },
  {
    question: "Can I attach evidence on Free?",
    answer:
      "Free users can attach external links or references to evidence held in their existing systems. Direct document or evidence uploads are not included in Free.",
  },
  {
    question: "How many privacy documents can I generate on Free?",
    answer:
      "Free includes 1 successful privacy-document generation per workspace per calendar month. Paid plans will have higher allowances, with their exact numeric generation limits locked separately before paid launch.",
  },
  {
    question: "Which privacy laws will DataProtection.work support at launch?",
    answer:
      "The committed launch jurisdiction packs are Philippines / PH DPA and GDPR / EU. Canada and the United States are planned expansion markets and are not presented as supported launch packs today.",
  },
  {
    question: "How does DataProtection.work decide which privacy modules to activate?",
    answer:
      "Onboarding asks factual questions about where the business operates, where relevant people are located, where goods or services are offered, relevant monitoring activity, processing locations and other risk indicators. Deterministic, versioned rules then recommend modules and preserve the reason for activation. This is guidance, not a binding legal determination.",
  },
  {
    question: "Is GDPR only for EU companies?",
    answer:
      "No. GDPR can also be relevant to some organizations outside the EU depending on factors such as establishment, offering goods or services to people in the EU or EEA, and monitoring their behavior there. DataProtection.work asks factual applicability questions rather than using EU citizenship alone as the trigger.",
  },
  {
    question: "Does DataProtection.work guarantee compliance?",
    answer:
      "No. DataProtection.work provides structured, practical guidance to help you organize and improve privacy work. It does not guarantee legal or regulatory compliance, and some situations require professional or legal judgment.",
  },
  {
    question: "Does DataProtection.work replace legal advice?",
    answer:
      "No. The product is a privacy-management tool and guidance system, not a law firm. It can organize facts, requirements, records and actions, but it does not replace advice from a qualified professional where legal judgment is needed.",
  },
  {
    question: "Who handles subscription payments?",
    answer:
      "Freemius is planned to act as Merchant of Record for paid subscriptions and subscription-related support. Paid checkout will only be represented as available after that production flow is implemented and verified.",
  },
] as const;

export const footerLinks = {
  Product: [
    { label: "How It Works", href: "/#how-it-works" },
    { label: "Features", href: "/#capabilities" },
    { label: "Pricing", href: "/#pricing" },
  ],
  Resources: [{ label: "Resources", href: "/resources" }],
  Legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Use", href: "/terms" },
  ],
  Company: [
    { label: "Security & Trust", href: "/#trust" },
    { label: "Contact", href: "/contact" },
  ],
} as const;
